"use client";

// Error components must be Client Components
import { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, ShieldAlert } from "lucide-react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { extractHttpStatus } from "@/lib/http-error-status";

type ErrorPageProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function ErrorPage(props: Readonly<ErrorPageProps>) {
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const router = useRouter();

  const errorObj = props.error as unknown as Record<string, unknown> & {
    status?: number;
    name?: string;
  };
  const detectedStatus = extractHttpStatus(props.error);

  const isApiError =
    errorObj?.name === "ApiError" || detectedStatus !== undefined;
  const is403 = detectedStatus === 403;
  const is401 = detectedStatus === 401;
  const is404 = detectedStatus === 404;
  const is400 = detectedStatus === 400;
  const is4xx =
    detectedStatus !== undefined &&
    detectedStatus >= 400 &&
    detectedStatus < 500 &&
    !is401 &&
    !is403 &&
    !is404 &&
    !is400;

  useEffect(() => {
    // Log the error to an error reporting service
    try {
      // Try to parse error message as JSON first
      if (props.error?.message) {
        try {
          const parsed = JSON.parse(props.error.message);
          setErrorMessage(JSON.stringify(parsed, null, 2));
          console.error("Error details:", parsed);
        } catch {
          // If not JSON, use message as-is
          setErrorMessage(props.error.message);
          console.error("Error:", props.error);
        }
      } else {
        // Fallback: stringify the entire error object
        const errorObj: Record<string, unknown> = {
          name: props.error.name,
          message: props.error.message,
          stack: props.error.stack,
        };

        // Add ApiError-specific properties if present
        if (isApiError) {
          const apiError = props.error as unknown as Record<string, unknown> & {
            status?: number;
            statusText?: string;
            url?: string;
            body?: unknown;
          };
          if (apiError.status !== undefined) errorObj.status = apiError.status;
          if (apiError.statusText !== undefined) errorObj.statusText = apiError.statusText;
          if (apiError.url !== undefined) errorObj.url = apiError.url;
          if (apiError.body !== undefined) errorObj.body = apiError.body;
        }

        setErrorMessage(JSON.stringify(errorObj, null, 2));
        console.error("Error:", props.error);
      }
    } catch {
      setErrorMessage(props.error?.message || "Unknown error occurred");
      console.error("Error:", props.error);
    }
  }, [props.error, isApiError]);

  // Determine error title and description based on status
  const getErrorInfo = () => {
    if (is403) {
      return {
        statusCode: "403",
        title: "Access Forbidden",
        description: "You don't have permission to access this resource",
        icon: ShieldAlert,
        iconColor: "text-yellow-600 dark:text-yellow-400",
        bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
        borderColor: "border-yellow-200 dark:border-yellow-800",
        textColor: "text-yellow-900 dark:text-yellow-100",
        detailTextColor: "text-yellow-700 dark:text-yellow-300",
      };
    }
    if (is401) {
      return {
        statusCode: "401",
        title: "Unauthorized",
        description: "You need to sign in to access this resource",
        icon: ShieldAlert,
        iconColor: "text-orange-600 dark:text-orange-400",
        bgColor: "bg-orange-100 dark:bg-orange-900/20",
        borderColor: "border-orange-200 dark:border-orange-800",
        textColor: "text-orange-900 dark:text-orange-100",
        detailTextColor: "text-orange-700 dark:text-orange-300",
      };
    }
    if (is404) {
      return {
        statusCode: "404",
        title: "Not Found",
        description: "The requested resource could not be found",
        icon: AlertTriangle,
        iconColor: "text-blue-600 dark:text-blue-400",
        bgColor: "bg-blue-100 dark:bg-blue-900/20",
        borderColor: "border-blue-200 dark:border-blue-800",
        textColor: "text-blue-900 dark:text-blue-100",
        detailTextColor: "text-blue-700 dark:text-blue-300",
      };
    }
    if (is400) {
      return {
        statusCode: "400",
        title: "Invalid request",
        description: "The server could not process this request",
        icon: AlertTriangle,
        iconColor: "text-amber-600 dark:text-amber-400",
        bgColor: "bg-amber-100 dark:bg-amber-900/20",
        borderColor: "border-amber-200 dark:border-amber-800",
        textColor: "text-amber-900 dark:text-amber-100",
        detailTextColor: "text-amber-800 dark:text-amber-300",
      };
    }
    if (is4xx) {
      return {
        statusCode: String(detectedStatus ?? "4xx"),
        title: "Request could not be completed",
        description: "Something about this request was not accepted",
        icon: AlertTriangle,
        iconColor: "text-amber-600 dark:text-amber-400",
        bgColor: "bg-amber-100 dark:bg-amber-900/20",
        borderColor: "border-amber-200 dark:border-amber-800",
        textColor: "text-amber-900 dark:text-amber-100",
        detailTextColor: "text-amber-800 dark:text-amber-300",
      };
    }
    return {
      statusCode: "500",
      title: "Server Error",
      description: "Something went wrong on our end",
      icon: AlertTriangle,
      iconColor: "text-red-600 dark:text-red-400",
      bgColor: "bg-red-100 dark:bg-red-900/20",
      borderColor: "border-red-200 dark:border-red-800",
      textColor: "text-red-900 dark:text-red-100",
      detailTextColor: "text-red-700 dark:text-red-300",
    };
  };

  const errorInfo = getErrorInfo();
  const IconComponent = errorInfo.icon;

  const handleAction = () => {
    if (is401) {
      router.push("/auth/sign-in");
    } else if (is403) {
      router.back();
    } else {
      props.reset();
    }
  };

  return (
    <div className="container relative z-10 flex min-h-screen grow flex-col items-center justify-center px-0">
      <div className="animate-fadeInDown">
        <Card className="w-full max-w-2xl overflow-hidden rounded-none border-0 bg-background shadow-2xl backdrop-blur-sm sm:w-[600px] sm:rounded-xl">
          <CardHeader className="pb-6 text-center">
            <div className={`mx-auto mb-4 flex size-16 items-center justify-center rounded-full ${errorInfo.bgColor}`}>
              <IconComponent className={`size-8 ${errorInfo.iconColor}`} />
            </div>
            <CardTitle className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              {errorInfo.statusCode} - {errorInfo.title}
            </CardTitle>
            <CardDescription className="mt-2 text-lg text-gray-600 dark:text-gray-300">
              {errorInfo.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className={`rounded-lg border p-4 ${errorInfo.borderColor} ${errorInfo.bgColor}`}>
              <div className="flex items-start space-x-3">
                <IconComponent className={`mt-0.5 size-5 shrink-0 ${errorInfo.iconColor}`} />
                <div>
                  <h3 className={`font-semibold ${errorInfo.textColor}`}>
                    What happened?
                  </h3>
                  <p className={`mt-1 text-sm ${errorInfo.detailTextColor}`}>
                    {is403
                      ? "You don't have the necessary permissions to access this resource. Please contact your administrator if you believe this is an error."
                      : is401
                        ? "Your session may have expired or you need to sign in. Please sign in to continue."
                        : is404
                          ? "The page or resource you're looking for doesn't exist or has been moved."
                          : is400
                            ? "The app sent a request the API rejected (for example billing, entitlements, or invalid parameters). Try again, or check account and subscription settings."
                            : is4xx
                              ? "The server declined this request. Try going back or refreshing. If it keeps happening, contact support with the details below."
                              : "We encountered an unexpected error while processing your request. This has been logged and our team will investigate."}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                What you can do:
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    <RefreshCw className="size-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Try again
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Click the button below to retry the operation
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    <ChevronDown className="size-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      View error details
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Click below to see technical details about the error
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Collapsible Error Details */}
            <div className="space-y-3">
              <button
                onClick={() => setShowErrorDetails(!showErrorDetails)}
                className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
              >
                <span className="font-medium text-gray-900 dark:text-white">
                  {showErrorDetails ? "Hide" : "Show"} error details
                </span>
                {showErrorDetails ? (
                  <ChevronUp className="size-4 text-gray-500" />
                ) : (
                  <ChevronDown className="size-4 text-gray-500" />
                )}
              </button>

              {showErrorDetails && (
                <div className="rounded-lg border border-gray-700 bg-gray-900 p-4">
                  <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-gray-300">
                    {errorMessage || "No error details available"}
                  </pre>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                <strong>Need help?</strong> If this problem persists, please
                contact our support team.
              </p>
            </div>

            <div className="flex justify-center gap-3 pt-4">
              {is401 ? (
                <Button
                  onClick={handleAction}
                  className="flex items-center space-x-2 px-6 py-2"
                >
                  <span>Sign In</span>
                </Button>
              ) : is403 ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => router.back()}
                    className="flex items-center space-x-2 px-6 py-2"
                  >
                    <span>Go Back</span>
                  </Button>
                  <Button
                    onClick={() => router.push("/dashboard")}
                    className="flex items-center space-x-2 px-6 py-2"
                  >
                    <span>Go to Dashboard</span>
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleAction}
                  className="flex items-center space-x-2 px-6 py-2"
                >
                  <RefreshCw className="size-4" />
                  <span>Try Again</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
